import classNames from "classnames"
import { ElementType, ReactNode } from "react"
import { LayoutContext } from "./LayoutContext"
import { devWarning } from "../utils/devWarning"
import { LayoutHeader } from "./LayoutHeader"
import { LayoutProps } from "."
import { useLayoutAncestorContext, LayoutAncestorContextProvider } from "./LayoutAncestorContext"
import { LayoutVariation } from "./types"

// By default all layouts are left aligned unless specified in this array
const CONTENT_JUSTIFY_CENTER_LAYOUTS: LayoutVariation[] = ["form", "tools", "wizard"]
/**
 * This component implements paddings based on our Layout rules.
 * This component should wrap your Content and Heading areas.
 *
 * Previous to layouts, there was no reliable way of creating pages that had consistent spacing in our tooling. As such, pages with similar layouts have been built with different spacing, or different typography. This has resulted in many visual inconsistencies across the app.
 *
 * As of writing, all designers are now using these layouts as templates to build new pages. Developers should aim to use the Layout component to apply the correct paddings to legacy pages as they see fit.
 */
export function Layout<T extends "main" | "div" = "main">({
  align,
  as,
  children,
  overrideMaxWidth,
  className,
  elementRef: ref,
  variation,
  ...rest
}: LayoutProps<T>) {
  const isNestedInOtherLayout = useLayoutAncestorContext()

  devWarning(
    isNestedInOtherLayout,
    "<Layout/> is nested in another <Layout/>. Check how the React component tree rendered",
  )

  const Component = as || ("main" as ElementType)
  const isContentJustifyCenter = align
    ? align === "center"
    : variation && CONTENT_JUSTIFY_CENTER_LAYOUTS.includes(variation)

  return (
    <LayoutContext.Provider value={{ variation }}>
      <LayoutAncestorContextProvider>
        <Component
          data-testid="ds-layout-main"
          className={classNames("min-h-screen w-full p-4 md:p-8 lg:p-10", className)}
          ref={ref}
          {...rest}
        >
          <div
            className={classNames(
              isContentJustifyCenter && "mx-auto",
              !overrideMaxWidth && "max-w-screen-container",
              "pb-16", // bottom padding accounts for Pendo button which is used across app
            )}
          >
            {children}
          </div>
        </Component>
      </LayoutAncestorContextProvider>
    </LayoutContext.Provider>
  )
}

const LayoutActionButtons = ({ children }: { children: ReactNode }) => {
  return <>{children}</>
}

Layout.ActionButtons = LayoutActionButtons
Layout.Header = LayoutHeader
